import { Grid } from '@material-ui/core';
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Icons,
  Typography,
} from '@nutrien/cxp-components';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';

import EmptyBoxIcon from '../../assets/icons/emptyBoxIcon';
import { useMst } from '../../mobx-models/Root';
import { InspectionTaskListType } from '../../models/models';
import type { ViewInspectorDocument } from '../../rxdb/viewInspector/viewInspector.types';
import { inspectionListSort } from '../../utilities/sortHelper';
import { getValueFromLocalStorage } from '../../utilities/utilityFunctions';
import AssignInspector from '../AssignInspector';
import AssignToMeDialog from '../AssignToMeDialog';
import useStyles from './UnAssigned.styles';
interface Props {
  inspectionListData: InspectionTaskListType[];
  searchText: string;
  sortInspectionId: string;
  inspectionName: string;
  needAttention: string;
  inspectionLevel: string;
  inspectorData: ViewInspectorDocument[];
  isAssignToLead: boolean;
}

const UnAssigned: React.FC<Props> = ({
  inspectionListData,
  searchText,
  sortInspectionId,
  inspectionName,
  needAttention,
  inspectionLevel,
  inspectorData,
  isAssignToLead,
}) => {
  const classes = useStyles();
  const { HandleGesture, CurrentUserSelected } = useMst();
  const [openAssignInspector, setOpenAssignInspector] = useState<boolean>(false);
  const [inspectionId, setInspectionId] = useState<number>(0);
  const [inspectionLevelComment, setInspectionLevelComment] = useState<string>('');
  const [openAssignToMeDialog, setOpenAssignToMeDialog] = useState<boolean>(false);
  const [timeOutId, setTimeoutId] = useState<NodeJS.Timeout | null | number>(-1);
  const [midId, setMidId] = useState<number>(0);
  const [onHold, setOnHold] = useState<boolean>(false);

  const handleAssignFieldInspector = (
    event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
    inspId: number,
    comment: string,
  ) => {
    setInspectionId(inspId);
    setInspectionLevelComment(comment);
    if (isAssignToLead) setOpenAssignInspector(true);
    else {
      setOpenAssignToMeDialog(true);
      HandleGesture.insertIntoInspectionId(inspId);
    }
    event.stopPropagation();
  };

  const onPress = (id: number) => {
    setMidId(id);
    if (!HandleGesture.gestureForUnAssigned) {
      const tid: NodeJS.Timeout | null = setTimeout(() => {
        setOnHold(true);
        HandleGesture.insertIntoInspectionId(id);
        HandleGesture.setGestureForUnAssigned(true);
      }, 1000);
      setTimeoutId(tid);
    }
  };

  const onClickUp = (id: number) => {
    if (HandleGesture.gestureForUnAssigned && midId === id) {
      if (HandleGesture.inspectionIds.find(element => element === id) !== undefined) {
        HandleGesture.removeFromInspectionId(id);
      } else {
        HandleGesture.insertIntoInspectionId(id);
      }
    }
    if (!HandleGesture.gestureForUnAssigned) {
      clearTimeout(timeOutId as NodeJS.Timeout);
    }
  };

  const onRelease = (e: React.TouchEvent<HTMLDivElement>, id: number) => {
    onClickUp(id);
    e.preventDefault();
  };

  return (
    <Card elevation={1} className={classes.outerCard}>
      {inspectionListData.length === 0 ? (
        <Grid className={classes.messageGrid}>
          <EmptyBoxIcon className={classes.emptyBox} style={{ fontSize: 58 }} />
          <Typography variant="h6" color="textSecondary" className={classes.divText}>
            It's empty. There are no Unassigned Inspections left in the Work Package.
          </Typography>
        </Grid>
      ) : (
        inspectionListData
          .filter(
            inspection =>
              inspection?.assetName?.toLowerCase().includes(searchText?.toLowerCase()) ||
              inspection.inspectionId.toString()?.toLowerCase().includes(searchText?.toLowerCase()),
          )
          .sort((inspection1, inspection2) =>
            inspectionListSort(
              inspection1,
              inspection2,
              sortInspectionId,
              inspectionName,
              needAttention,
              inspectionLevel,
            ),
          )
          .map(inspection => {
            return (
              <Grid className={classes.unAssignedContainer} key={inspection.inspectionId}>
                <Card
                  data-testid={`${'inspection-div-'}${inspection.inspectionId}`}
                  elevation={1}
                  className={`${classes.innerCard} ${
                    HandleGesture.inspectionIds.find(
                      element => element === Number(inspection.inspectionId),
                    ) !== undefined
                      ? 'cardWithColor'
                      : ''
                  }`}
                  key={inspection.inspectionId}
                  onMouseDown={() => onPress(Number(inspection.inspectionId))}
                  onMouseMove={() => setMidId(-1)}
                  onMouseUp={() => {
                    const inspId = Number(inspection.inspectionId);
                    if (inspId) setMidId(inspId);
                    if (onHold) setOnHold(false);
                    else onClickUp(inspId);
                  }}
                  onTouchStart={() => onPress(Number(inspection.inspectionId))}
                  onTouchMove={() => setMidId(-1)}
                  onTouchEnd={e => {
                    if (onHold) setOnHold(false);
                    else onRelease(e, Number(inspection.inspectionId));
                  }}
                >
                  <Grid className={classes.pointer}>
                    <Grid item container xs={12} alignItems="center">
                      <Grid item container xs={HandleGesture.gestureForUnAssigned ? 11 : 8}>
                        {HandleGesture.gestureForUnAssigned && (
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    HandleGesture.inspectionIds.find(
                                      element => element === Number(inspection.inspectionId),
                                    ) !== undefined
                                  }
                                />
                              }
                              label=""
                            />
                          </Grid>
                        )}
                        <Grid item xs={11}>
                          <Typography variant="h5" display="inline">
                            {`Id: ${inspection.inspectionId} - ${inspection?.assetName}`}
                          </Typography>
                          <span className={classes.greenDot} />

                          <Typography
                            display="inline"
                            variant="h5"
                            fontWeight="normal"
                            className={classes.locationStyle}
                          >
                            {inspection.areaList.length > 0 ? inspection.areaList[0].name : ''}
                          </Typography>
                          {inspection?.assetDescription?.length > 0 && (
                            <Typography variant="subtitle2" fontWeight="normal">
                              {inspection.assetDescription}
                            </Typography>
                          )}
                          <Typography
                            variant="subtitle2"
                            fontWeight="normal"
                            className={classes.locationStyle}
                          >
                            {inspection.assetInspectionType}
                          </Typography>
                        </Grid>
                      </Grid>

                      {!HandleGesture.gestureForUnAssigned && (
                        <Grid item xs={3}>
                          <Grid container justifyContent="flex-end" alignItems="center">
                            <Grid item className={classes.assignGrid}>
                              <Button
                                color="primary"
                                noMinHeight
                                onMouseUp={e => {
                                  handleAssignFieldInspector(
                                    e,
                                    Number(inspection.inspectionId),
                                    inspection.inspectionAssignmentComment,
                                  );
                                }}
                                onMouseDown={e => e.stopPropagation()}
                                onTouchStart={e => e.stopPropagation()}
                                onTouchEnd={e => {
                                  handleAssignFieldInspector(
                                    e,
                                    Number(inspection.inspectionId),
                                    inspection.inspectionAssignmentComment,
                                  );
                                  e.preventDefault();
                                }}
                              >
                                <Typography variant="subtitle2">
                                  {isAssignToLead ? 'Assign' : 'Assign To Me'}
                                </Typography>
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                      <Grid item xs={1}>
                        <Grid container justifyContent="flex-end" alignItems="center">
                          <Icons.ChevronRightFeather color="primary" />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            );
          })
      )}

      {openAssignInspector && (
        <AssignInspector
          title="Assign Inspector"
          isUpdateInspector={false}
          openAssignInspector={openAssignInspector}
          setOpenAssignInspector={setOpenAssignInspector}
          fieldInspectorList={[
            {
              fieldInspectorName: 'Assigned To Me',
              fieldInspectorId: CurrentUserSelected?.currentUser?.inspectorId,
            },
            ...JSON.parse(JSON.stringify(inspectorData)),
          ]}
          leadInspectorId={Number(getValueFromLocalStorage('inspectorId'))}
          inspectionId={inspectionId}
          inspectionLevelComment={inspectionLevelComment}
          isAssignToLead={isAssignToLead}
        />
      )}
      {openAssignToMeDialog && (
        <AssignToMeDialog
          open={openAssignToMeDialog}
          onClose={() => {
            HandleGesture.resetInspectionId();
            setOpenAssignToMeDialog(false);
          }}
          isAssignToLead={isAssignToLead}
        />
      )}
    </Card>
  );
};

export default observer(UnAssigned);
